import { useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { ThemeColor } from '@view-model/models/common/color';
import { ColorButton } from '@view-model/ui/components/View/ViewTitleMenu/ColorMenuButton/ColorButton';

type Props = {
  onChangeColor(color: ThemeColor): void;
  onOpenSubMenu(menuType: string): void;
};

export const ColorMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onChangeColor, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('color');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.color')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute left-full top-0 z-50 w-max rounded-lg bg-white py-4 shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.White} />
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.Red} />
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.Green} />
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.Blue} />
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.Orange} />
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.Yellow} />
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.YellowGreen} />
        <ColorButton onSelectColor={onChangeColor} color={ThemeColor.Purple} />
      </div>
    </div>
  );
});

ColorMenuButton.displayName = 'ColorMenuButton';
